/**
 * Account List View Queries
 *
 * Same pattern as Client list view: list views + records for ListViewTable.
 * Used so Account UI matches Client UI (view dropdown, filter, table, pagination).
 * When shouldUseGobodo(), records come from Gobodo via getRecordsForObject('Account').
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { getRecordsForObjectPage } from '../recordStorageService';
import { persistedAccountListViews, persistedAccountRecords } from '../mockData/accounts';
import { genericRecordToAccount } from './accountQueries';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../../config/rdsConfig';
import type { ListView, ListViewRecord, ListViewData } from '../../types/listView';
import type { Account } from '../../types/index';

function accountToRecord(account: Account): ListViewRecord {
  return {
    id: account.id,
    name: account.name,
    type: account.type,
    description: account.description ?? '',
    accountOwner: account.accountOwner ?? '',
    phone: account.phone ?? '',
    email: account.email ?? '',
    billingAddress: account.billingAddress ?? '',
  };
}

export function useAccountListViews() {
  return useQuery({
    queryKey: ['accountListViews'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return persistedAccountListViews.get();
    },
    staleTime: 5 * 60 * 1000,
  });
}

const DEFAULT_PAGE_SIZE = 25;

export function useAccountListViewData(
  listViewId: string,
  options?: { page?: number; pageSize?: number }
) {
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const useBackend = shouldUseGobodo() || (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject('Account'));

  return useQuery<ListViewData>({
    queryKey: ['accountListViewData', listViewId, page, pageSize],
    queryFn: async () => {
      const allViews = persistedAccountListViews.get();
      const listView = allViews.find(lv => lv.id === listViewId);
      if (!listView) throw new Error('List view not found');

      if (useBackend) {
        const { records: pageRecords, totalRecords } = await getRecordsForObjectPage('Account', { page, size: pageSize });
        const accounts: Account[] = pageRecords.map(genericRecordToAccount);
        let records: ListViewRecord[] = accounts.map(accountToRecord);

        if (listView.filters?.length) {
          listView.filters.forEach(filter => {
            records = records.filter(record => {
              const value = record[filter.fieldApiName];
              if (filter.operator === 'equals') return value === filter.value;
              return true;
            });
          });
        }

        if (listView.sortBy) {
          records = [...records].sort((a, b) => {
            const aVal = a[listView.sortBy!.fieldApiName];
            const bVal = b[listView.sortBy!.fieldApiName];
            const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
            return listView.sortBy!.direction === 'desc' ? -cmp : cmp;
          });
        }

        return { listView, records, totalRecords };
      }

      const allAccounts = persistedAccountRecords.get();
      let filtered: ListViewRecord[] = allAccounts.map(accountToRecord);
      if (listView.filters?.length) {
        listView.filters.forEach(filter => {
          filtered = filtered.filter(record => {
            const value = record[filter.fieldApiName];
            if (filter.operator === 'equals') return value === filter.value;
            return true;
          });
        });
      }
      if (listView.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          const aVal = a[listView.sortBy!.fieldApiName];
          const bVal = b[listView.sortBy!.fieldApiName];
          const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
          return listView.sortBy!.direction === 'desc' ? -cmp : cmp;
        });
      }
      const totalRecords = filtered.length;
      const start = page * pageSize;
      const records = filtered.slice(start, start + pageSize);
      return { listView, records, totalRecords };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!listViewId,
    placeholderData: keepPreviousData,
  });
}

export function useDefaultAccountListView() {
  return useQuery({
    queryKey: ['defaultAccountListView'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));
      const allViews = persistedAccountListViews.get();
      return allViews.find(lv => lv.isDefault) || allViews[0];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateAccountListView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      const views = persistedAccountListViews.get();
      const index = views.findIndex(v => v.id === id);
      if (index === -1) throw new Error('List view not found');
      const updated = { ...views[index], ...updates };
      views[index] = updated;
      persistedAccountListViews.set(views);
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accountListViews'] });
      queryClient.invalidateQueries({ queryKey: ['accountListViewData'] });
    },
  });
}
